import React from 'react';
import { ArrowRightIcon, CheckCircleIcon, StarIcon, ShieldCheckIcon, ZapIcon, UsersIcon, TrendingUpIcon, AwardIcon, ActivityIcon } from 'lucide-react';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="flex flex-col min-h-screen" data-id="element-127">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden py-20 lg:py-32" data-id="element-128">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-emerald-50 opacity-50 blur-3xl" data-id="element-129"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-emerald-100 opacity-40 blur-3xl" data-id="element-130"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-131">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-132">
            {/* Text Content */}
            <div className="text-left" data-id="element-133">
              <span className="inline-block py-1 px-3 rounded-full bg-emerald-50 text-emerald-600 font-semibold tracking-wider uppercase text-xs mb-6 border border-emerald-100 shadow-sm" data-id="element-134">
                Powering Your Digital Airtime Experience
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight" data-id="element-135">
                Instant Airtime, <br className="hidden md:block" data-id="element-136" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-emerald-700" data-id="element-137">
                  Anytime, Anywhere
                </span>{' '}
                <br className="hidden md:block" data-id="element-138" />
                in Kenya
              </h1>
              <p className="text-xl text-gray-600 mb-10 max-w-lg leading-relaxed" data-id="element-139">
                Top up any network in seconds. Safaricom, Airtel, Telkom — all
                at your fingertips with secure M-PESA payments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-140">
                <button onClick={() => onNavigate('contact')} className="inline-flex justify-center items-center px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5" data-id="element-141">
                  Get Started
                </button>
                <button onClick={() => onNavigate('services')} className="inline-flex justify-center items-center px-8 py-4 bg-white border-2 border-gray-200 hover:border-emerald-500 hover:text-emerald-600 text-gray-700 font-medium rounded-xl transition-all duration-200" data-id="element-142">
                  Learn More
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-143" />
                </button>
              </div>
            </div>

            {/* Image Content */}
            <div className="relative lg:ml-auto w-full max-w-lg mx-auto lg:max-w-none" data-id="element-144">
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-400 to-emerald-200 rounded-3xl transform rotate-3 scale-105 opacity-20 blur-lg" data-id="element-145"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20" data-id="element-146">
                <img src="https://cdn.magicpatterns.com/uploads/aSX1WwvkFskPr9R4e8Xsp4/pexels-gistsandthrills-4252245.jpg" alt="Two people using smartphones" className="w-full h-auto object-cover aspect-[4/3] lg:aspect-square" data-id="element-147" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-148"></div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-4 animate-bounce" style={{
              animationDuration: '3s'
            }} data-id="element-149">
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center" data-id="element-150">
                  <ZapIcon className="h-6 w-6 text-emerald-600" data-id="element-151" />
                </div>
                <div data-id="element-152">
                  <p className="text-sm text-gray-500 font-medium" data-id="element-153">
                    Instant Delivery
                  </p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-154">
                    &lt; 10 Seconds
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 border-y border-gray-100 relative z-20" data-id="element-155">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-156">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-gray-100" data-id="element-157">
            <div className="p-6 flex flex-col items-center" data-id="element-158">
              <ActivityIcon className="h-8 w-8 text-emerald-400 mb-4" data-id="element-159" />
              <div className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight" data-id="element-160">
                98%
              </div>
              <div className="text-gray-500 font-medium" data-id="element-161">Success Rates</div>
            </div>
            <div className="p-6 flex flex-col items-center" data-id="element-162">
              <UsersIcon className="h-8 w-8 text-emerald-400 mb-4" data-id="element-163" />
              <div className="text-4xl font-extrabold text-emerald-600 mb-2 tracking-tight" data-id="element-164">
                100K+
              </div>
              <div className="text-gray-500 font-medium" data-id="element-165">
                Satisfied Customers
              </div>
            </div>
            <div className="p-6 flex flex-col items-center" data-id="element-166">
              <TrendingUpIcon className="h-8 w-8 text-emerald-400 mb-4" data-id="element-167" />
              <div className="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight" data-id="element-168">
                100,000+
              </div>
              <div className="text-gray-500 font-medium" data-id="element-169">
                Worth of airtime sold nationwide
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-gray-50" data-id="element-170">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-171">
          <div className="text-center mb-16" data-id="element-172">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-173">
              Explore Our Airtime Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-174">
              Everything you need to stay connected, tailored for your
              convenience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-175">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group" data-id="element-176">
              <div className="h-14 w-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300" data-id="element-177">
                <ZapIcon className="h-7 w-7 text-emerald-500 group-hover:text-white transition-colors duration-300" data-id="element-178" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-179">
                Instant Top-ups
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-180">
                Recharge any Kenyan network in under 10 seconds. No delays, no
                hidden charges.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group" data-id="element-181">
              <div className="h-14 w-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300" data-id="element-182">
                <CheckCircleIcon className="h-7 w-7 text-emerald-500 group-hover:text-white transition-colors duration-300" data-id="element-183" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-184">
                Simple Process
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-185">
                Select your network, enter the amount, pay via M-PESA. Done in
                three taps.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group" data-id="element-186">
              <div className="h-14 w-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300" data-id="element-187">
                <AwardIcon className="h-7 w-7 text-emerald-500 group-hover:text-white transition-colors duration-300" data-id="element-188" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-189">
                Trusted by Thousands
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-190">
                Over 100,000 Kenyans rely on us daily for fast, dependable
                airtime delivery.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group" data-id="element-191">
              <div className="h-14 w-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300" data-id="element-192">
                <ShieldCheckIcon className="h-7 w-7 text-emerald-500 group-hover:text-white transition-colors duration-300" data-id="element-193" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-194">
                Bank-Grade Security
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-195">
                Your transactions are protected with enterprise-level encryption
                and fraud prevention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 bg-white" data-id="element-196">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197">
          <div className="bg-emerald-900 rounded-3xl overflow-hidden shadow-2xl relative min-h-[500px]" data-id="element-198">
            <div className="absolute inset-0 opacity-50 mix-blend-overlay" data-id="element-199">
              <img src="https://cdn.magicpatterns.com/uploads/tzh8koZZuVSsLTk2ooomip/pexels-kampus-7983200.jpg" alt="Kenyan family taking a selfie together" className="w-full h-full object-cover" data-id="element-200" />
            </div>
            <div className="relative z-10 px-8 py-24 md:py-32 md:px-16 lg:w-2/3" data-id="element-201">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-202">
                Built for Kenya, <br data-id="element-203" />
                by Kenyans
              </h2>
              <p className="text-lg md:text-xl text-emerald-50 mb-8 leading-relaxed max-w-xl" data-id="element-204">
                We understand the Kenyan telecommunications landscape inside
                out. From bustling Nairobi streets to rural communities, our
                platform ensures everyone stays connected.
              </p>
              <button onClick={() => onNavigate('about')} className="inline-flex items-center px-6 py-3 bg-white text-emerald-900 font-semibold rounded-lg hover:bg-emerald-50 transition-colors duration-200" data-id="element-205">
                Our Story
                <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-206" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50" data-id="element-207">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-208">
          <div className="text-center mb-16" data-id="element-209">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-210">
              Client Feedback
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-211">
              Don't just take our word for it. Here's what our users have to
              say.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-212">
            {/* Testimonial 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300" data-id="element-213">
              <div className="flex text-amber-400 mb-6" data-id="element-214">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-current" data-id="element-215" />)}
              </div>
              <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-216">
                "Prompt Airtime has completely changed how I buy airtime. It's
                incredibly fast, and I can top up my phone anytime, anywhere.
                The occasional discounts are a fantastic bonus! Highly
                recommended."
              </p>
              <div className="flex items-center mt-auto" data-id="element-217">
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg" data-id="element-218">
                  JM
                </div>
                <div className="ml-4" data-id="element-219">
                  <div className="font-bold text-gray-900" data-id="element-220">James Mwangi</div>
                  <div className="text-sm text-gray-500 font-medium" data-id="element-221">
                    Nairobi
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300" data-id="element-222">
              <div className="flex text-amber-400 mb-6" data-id="element-223">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-current" data-id="element-224" />)}
              </div>
              <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-225">
                "As a business owner, managing airtime for my team used to be a
                headache. Prompt Airtime is a game-changer—quick, secure, and
                affordable. Their customer support is always responsive and
                helpful!"
              </p>
              <div className="flex items-center mt-auto" data-id="element-226">
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg" data-id="element-227">
                  SO
                </div>
                <div className="ml-4" data-id="element-228">
                  <div className="font-bold text-gray-900" data-id="element-229">Sarah Ochieng</div>
                  <div className="text-sm text-gray-500 font-medium" data-id="element-230">
                    Business Owner
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300" data-id="element-231">
              <div className="flex text-amber-400 mb-6" data-id="element-232">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-current" data-id="element-233" />)}
              </div>
              <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-234">
                "I've been using Prompt Airtime for months now, and I'm
                consistently impressed by how reliable the service is. It's so
                simple to use, I never have to worry about running out of
                airtime during important calls."
              </p>
              <div className="flex items-center mt-auto" data-id="element-235">
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg" data-id="element-236">
                  MN
                </div>
                <div className="ml-4" data-id="element-237">
                  <div className="font-bold text-gray-900" data-id="element-238">
                    Michael Nyongesa
                  </div>
                  <div className="text-sm text-gray-500 font-medium" data-id="element-239">
                    Frequent User
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-240">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-50 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-241"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-242">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight" data-id="element-243">
            Ready to Stay Connected?
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-244">
            Join thousands of Kenyans who trust Prompt Airtime for fast,
            affordable, and secure airtime purchases.
          </p>
          <button onClick={() => onNavigate('contact')} className="inline-flex items-center px-10 py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg rounded-xl transition-all duration-200 shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:-translate-y-1" data-id="element-245">
            Get Started Today
            <ArrowRightIcon className="ml-2 h-6 w-6" data-id="element-246" />
          </button>
        </div>
      </section>
    </div>;
}